import { Dispatch, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { ClientContext, useManualQuery } from 'graphql-hooks'

import { getCompanyAndUserInfo } from '../../../../graphql/custom-queries'
import { getCompanyAndUser } from '../../../../services/api/companies'
import { updateSlackToken } from '../../../../services/api/slack'
import { useAppDispatch } from '../../../../store/hooks'
import { setUserId } from '../../../../store/user-id-slice'
import { setAuthState } from '../../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { confirmSigninWithCustomChallengeAnswer, getAuthTokens, IAuthNextStep, signin } from '@vacationtracker/shared/services/auth'

import { IGetCompanyAndUserInfo } from '../../../../types/custom-queries'
import { rememberProviderAccount, saveRememberedLogin } from '../../../../util/remembered-login'
import { forgetAccessRequest } from '../../../../util/access-requests'
import { SIGNIN_SUCCESS_EVENT, trackLoginCompleted, trackSignin } from '../analytics'
import { NewSigninAction } from '../types'
import { ISigninModals } from './use-signin-modals'
import { ADP } from './use-platform-error-handler'

export interface ICompleteLoginParams {
  username: string
  email: string
  /** ADP is a way into an email account rather than a platform of its own. */
  platform: PlatformEnum | typeof ADP
  analyticsPayload: Record<string, unknown>
}

export interface ISigninSession {
  completeLogin: (params: ICompleteLoginParams) => Promise<void>
  signinWithPlatformToken: (params: IPlatformTokenSigninParams) => Promise<void>
}

export interface IPlatformTokenSigninParams {
  username: string
  token: string
  platform: string
  email: string
  botToken?: string
}

export interface IUseSigninSessionParams {
  localDispatch: Dispatch<NewSigninAction>
  modals: ISigninModals
}

/**
 * The terminal half of every sign-in path. This is the only place that writes to the
 * shared store, and only once authentication has actually succeeded, so an abandoned
 * or failed attempt leaves the rest of the app untouched.
 */
export const useSigninSession = ({ localDispatch, modals }: IUseSigninSessionParams): ISigninSession => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const gqlClient = useContext(ClientContext)
  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const completeLogin = async ({ username, email, platform, analyticsPayload }: ICompleteLoginParams) => {
    localStorage.setItem('userId', username)
    if (email) {
      // An ADP sign-in is into an email account, so it is remembered as one, with what the
      // password step needs to offer ADP again. The marker refuses a platform it does not know.
      if (platform === ADP) {
        saveRememberedLogin({ email, platform: PlatformEnum.Email, username, adpEnabled: true })
      } else {
        saveRememberedLogin({ email, platform })
        rememberProviderAccount({ email, platform })
      }
      forgetAccessRequest(email)
    }
    dispatch(setUserId(username))
    await getCompanyAndUser(username, getCompanyAndUserInfoQuery, dispatch, history)
    trackLoginCompleted(analyticsPayload as Record<string, string>)
    // Closes the per-platform funnel by name, next to the LOGIN_COMPLETED the dashboards read.
    const successEvent = SIGNIN_SUCCESS_EVENT[platform]
    if (successEvent) {
      trackSignin(successEvent, { platform })
    }
    dispatch(setAuthState(AuthStateEnum.signedIn))
  }

  const signinWithPlatformToken = async ({ username, token, platform, email, botToken }: IPlatformTokenSigninParams) => {
    const signInResponse = (await signin(username)) as IAuthNextStep
    // Thrown, not returned: every caller reads a normal return as a finished sign-in, and with
    // no login to complete the page would keep spinning. Their error paths free the form instead.
    if (signInResponse.challengeName !== 'CUSTOM_CHALLENGE' || signInResponse.challengeParam?.question !== 'token') {
      throw new Error(`UNEXPECTED_PLATFORM_SIGNIN_STEP: ${signInResponse.challengeName ?? signInResponse.signInStep ?? 'unknown'}`)
    }

    const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
    localStorage.setItem('userId', cognitoResponse.username)

    try {
      const authToken = await getAuthTokens()
      if (gqlClient && authToken.idToken) {
        gqlClient.setHeader('Authorization', authToken.idToken)
      }
    } catch (error) {
      // Swallow the error
    }

    if (platform === PlatformEnum.Slack) {
      try {
        await updateSlackToken(token, botToken)
      } catch (error) {
        const slackError = error?.response?.data?.error
        if (slackError === 'error.botTokenNotExist' || slackError === 'error.botTokenNotValid') {
          localDispatch({ type: 'busyChanged', isBusy: false })
          modals.showSlackLoginRequired(true)
          return
        }
      }
    }

    await completeLogin({
      username: cognitoResponse.username,
      email,
      platform: platform as PlatformEnum | typeof ADP,
      analyticsPayload: { platform },
    })
  }

  return { completeLogin, signinWithPlatformToken }
}
